import { useEffect, useRef, useState } from "react";
import { Bird } from "lucide-react";

// A soft, real garden birdsong ambience (CC0). Off by default; starts only on a
// user click (autoplay-policy safe), fades in gently and stays subtle.
export default function GardenSound() {
  const [on, setOn] = useState(false);
  const audioRef = useRef(null);
  const rafRef = useRef(0);
  const TARGET = 0.32;

  useEffect(() => {
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  const fade = (to, ms) => {
    const el = audioRef.current;
    if (!el) return;
    cancelAnimationFrame(rafRef.current);
    const from = el.volume;
    const start = performance.now();
    const step = (t) => {
      const p = Math.min(1, (t - start) / ms);
      el.volume = Math.max(0, Math.min(1, from + (to - from) * p));
      if (p < 1) rafRef.current = requestAnimationFrame(step);
      else if (to === 0) el.pause();
    };
    rafRef.current = requestAnimationFrame(step);
  };

  const toggle = async () => {
    const el = audioRef.current;
    if (!el) return;
    if (!on) {
      try {
        el.volume = 0;
        await el.play();
        fade(TARGET, 1400);
        setOn(true);
      } catch (e) {
        /* blocked */
      }
    } else {
      fade(0, 800);
      setOn(false);
    }
  };

  return (
    <>
      <audio ref={audioRef} src="/maker/ambience.mp3" loop preload="none" />
      <button
        type="button"
        onClick={toggle}
        data-cursor-hover
        data-testid="garden-sound-toggle"
        aria-pressed={on}
        aria-label={on ? "Turn garden sound off" : "Turn garden sound on"}
        className="fixed bottom-6 right-6 z-[60] flex items-center gap-2.5 rounded-full border border-maker-moss/20 bg-maker-dew/70 px-4 py-2.5 font-spacemono text-[0.6rem] uppercase tracking-[0.25em] text-maker-moss shadow-[0_16px_36px_-24px_rgba(74,82,67,0.6)] backdrop-blur-md transition-all duration-500 hover:border-maker-fern/50 hover:bg-maker-linen/80 md:bottom-8 md:right-8"
      >
        <Bird className={`h-4 w-4 text-maker-fern transition-transform duration-500 ${on ? "scale-110" : ""}`} />
        <span className="hidden sm:inline">{on ? "Garden · on" : "Garden sound"}</span>
        <span className="flex items-end gap-[2px]" aria-hidden>
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className={`w-[2px] rounded-full bg-maker-fern transition-all duration-300 ${on ? "gs-bar" : "h-[3px] opacity-40"}`}
              style={on ? { animationDelay: `${i * 0.18}s` } : undefined}
            />
          ))}
        </span>
      </button>
    </>
  );
}
